// Apolo IDE — Supabase project provisioning wizard (Sprint v11 E13 P8).
//
// 3-step modal:
//   1. List existing projects (sanity check token)
//   2. Create new project (name, region, db_pass)
//   3. Generate + apply schema (description → LLM → SQL → confirm → execute)
//
// Listens for `apolo:openSupabaseProvision` custom event to open.

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle2, Database, Loader2, Sparkles, X } from 'lucide-react';
import { apiFetch } from '../../lib/api.js';

const REGIONS = [
  { value: 'us-east-1', label: 'us-east-1 (N. Virginia)' },
  { value: 'us-west-1', label: 'us-west-1 (N. California)' },
  { value: 'sa-east-1', label: 'sa-east-1 (São Paulo)' },
  { value: 'eu-west-1', label: 'eu-west-1 (Ireland)' },
  { value: 'eu-central-1', label: 'eu-central-1 (Frankfurt)' },
];

export default function SupabaseProvisionModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState([]);
  const [projectsErr, setProjectsErr] = useState(null);
  const [loading, setLoading] = useState(false);

  // Step 2 form
  const [name, setName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [dbPass, setDbPass] = useState('');
  const [createdProject, setCreatedProject] = useState(null);
  const [createErr, setCreateErr] = useState(null);

  // Step 3 schema
  const [schemaDescription, setSchemaDescription] = useState('');
  const [schemaDraft, setSchemaDraft] = useState(null); // { sql, notes }
  const [schemaErr, setSchemaErr] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  const dialogRef = useRef(null);

  // External event opens the modal.
  useEffect(() => {
    function onOpen() {
      setOpen(true);
      setStep(1);
      setCreatedProject(null);
      setSchemaDraft(null);
      setApplyResult(null);
    }
    window.addEventListener('apolo:openSupabaseProvision', onOpen);
    return () => window.removeEventListener('apolo:openSupabaseProvision', onOpen);
  }, []);

  useEffect(() => {
    if (open && dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
    if (!open && dialogRef.current?.open) dialogRef.current.close();
  }, [open]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setProjectsErr(null);
    const res = await apiFetch('/api/integrations/supabase/projects');
    setLoading(false);
    if (!res.ok) {
      setProjectsErr(res.error || 'Falha ao listar projetos — verifique SUPABASE_PAT em Settings');
      return;
    }
    setProjects(Array.isArray(res.data?.projects) ? res.data.projects : []);
  }, []);

  useEffect(() => {
    if (open && step === 1) loadProjects();
  }, [open, step, loadProjects]);

  const handleClose = useCallback(() => setOpen(false), []);

  const createProject = useCallback(async () => {
    setLoading(true);
    setCreateErr(null);
    const res = await apiFetch('/api/integrations/supabase/projects', {
      method: 'POST',
      body: {
        name: name.trim(),
        organization_id: organizationId.trim(),
        region,
        db_pass: dbPass,
      },
    });
    setLoading(false);
    if (!res.ok) {
      setCreateErr(res.error || 'Falha ao criar');
      return;
    }
    setCreatedProject(res.data?.project);
    setStep(3);
  }, [name, organizationId, region, dbPass]);

  const generateSchema = useCallback(async () => {
    setLoading(true);
    setSchemaErr(null);
    const res = await apiFetch('/api/integrations/supabase/schema/generate', {
      method: 'POST',
      body: { description: schemaDescription },
    });
    setLoading(false);
    if (!res.ok) {
      setSchemaErr(res.error || 'Falha no LLM');
      return;
    }
    setSchemaDraft({ sql: res.data?.sql || '', notes: res.data?.notes || '' });
  }, [schemaDescription]);

  const applySchema = useCallback(async () => {
    if (!createdProject?.id || !schemaDraft?.sql) return;
    setLoading(true);
    setApplyResult(null);
    const res = await apiFetch(`/api/integrations/supabase/projects/${createdProject.id}/query`, {
      method: 'POST',
      body: { sql: schemaDraft.sql },
    });
    setLoading(false);
    setApplyResult({ ok: res.ok, error: res.error, data: res.data });
  }, [createdProject, schemaDraft]);

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      aria-labelledby="apolo-supabase-title"
      className="rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 p-0 text-apolo-textPrimary backdrop:bg-apolo-overlay"
    >
      <div className="flex w-[min(40rem,92vw)] flex-col gap-3 p-5">
        <header className="flex items-center justify-between">
          <h2 id="apolo-supabase-title" className="flex items-center gap-2 text-lg font-medium">
            <Database size={18} className="text-emerald-400" aria-hidden="true" />
            Supabase · Provisionamento
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            className="rounded-md p-1 text-apolo-textTertiary hover:text-apolo-textPrimary"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>

        <nav aria-label="Steps" className="flex items-center gap-1 text-[11px] text-apolo-textTertiary">
          {[1, 2, 3].map((s) => (
            <span
              key={s}
              aria-current={step === s ? 'step' : undefined}
              className={`rounded-full px-2 py-0.5 ${step === s ? 'bg-apolo-accent text-white' : 'bg-apolo-surface2'}`}
            >
              {s}. {s === 1 ? 'Projetos' : s === 2 ? 'Criar' : 'Schema'}
            </span>
          ))}
        </nav>

        {step === 1 && (
          <section className="space-y-2">
            {loading && (
              <div className="flex items-center gap-2 text-xs text-apolo-textTertiary">
                <Loader2 size={11} className="animate-spin" aria-hidden="true" /> Listando projetos…
              </div>
            )}
            {projectsErr && (
              <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-errorBg p-2 text-xs text-apolo-error">
                <AlertCircle className="mr-1 inline" size={11} aria-hidden="true" />
                {projectsErr}
              </div>
            )}
            {!loading && !projectsErr && (
              <>
                <ul className="max-h-48 overflow-y-auto rounded-md border border-apolo-borderSubtle text-xs">
                  {projects.length === 0 && (
                    <li className="p-3 text-apolo-textTertiary">Nenhum projeto ainda — crie um abaixo.</li>
                  )}
                  {projects.map((p) => (
                    <li key={p.id} className="flex items-center justify-between border-b border-apolo-borderSubtle px-3 py-1.5 last:border-b-0">
                      <span className="truncate text-apolo-textPrimary">{p.name}</span>
                      <span className="text-[10px] text-apolo-textTertiary">{p.region} · {p.status}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-apolo-accentHover"
                >
                  Criar novo projeto <ArrowRight size={11} aria-hidden="true" />
                </button>
              </>
            )}
          </section>
        )}

        {step === 2 && (
          <section className="space-y-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do projeto"
              className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
            />
            <input
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              placeholder="organization_id (Supabase Org ref)"
              className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
            />
            <div className="flex gap-2">
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
              >
                {REGIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <input
                type="password"
                value={dbPass}
                onChange={(e) => setDbPass(e.target.value)}
                placeholder="DB password (≥ 12 chars)"
                className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 py-1.5 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
              />
            </div>
            {createErr && (
              <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-errorBg p-2 text-xs text-apolo-error">
                {createErr}
              </div>
            )}
            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="rounded-md border border-apolo-borderSubtle px-3 py-1.5 text-xs text-apolo-textTertiary hover:text-apolo-textPrimary"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={createProject}
                disabled={loading || !name || !organizationId || !dbPass}
                className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-apolo-accentHover disabled:opacity-50"
              >
                {loading ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} aria-hidden="true" />}
                Criar projeto
              </button>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="space-y-2">
            {createdProject && (
              <div className="rounded-md border border-apolo-success/30 bg-apolo-successBg p-2 text-xs text-apolo-success">
                Projeto criado: {createdProject.name} · ref <code>{createdProject.id}</code>
              </div>
            )}
            <label htmlFor="apolo-schema-desc" className="text-[11px] font-medium text-apolo-textTertiary">
              Descreva o schema em linguagem natural
            </label>
            <textarea
              id="apolo-schema-desc"
              value={schemaDescription}
              onChange={(e) => setSchemaDescription(e.target.value)}
              rows={4}
              placeholder='ex.: "App de tarefas com users (auth.users), tasks (id, user_id FK, title, done, created_at), tags (id, user_id, name) e task_tags (FK pair). RLS por user_id."'
              className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg p-2 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
            />
            <button
              type="button"
              onClick={generateSchema}
              disabled={loading || !schemaDescription.trim()}
              className="inline-flex items-center gap-1 rounded-md border border-apolo-accent/40 bg-apolo-accentMuted px-3 py-1.5 text-xs font-medium text-apolo-accent hover:bg-apolo-accent/20 disabled:opacity-50"
            >
              {loading ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} aria-hidden="true" />}
              Gerar SQL via Sonnet
            </button>
            {schemaErr && (
              <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-errorBg p-2 text-xs text-apolo-error">
                {schemaErr}
              </div>
            )}
            {schemaDraft && (
              <>
                <label htmlFor="apolo-schema-sql" className="text-[11px] font-medium text-apolo-textTertiary">
                  SQL gerado (edite antes de aplicar)
                </label>
                <textarea
                  id="apolo-schema-sql"
                  value={schemaDraft.sql}
                  onChange={(e) => setSchemaDraft({ ...schemaDraft, sql: e.target.value })}
                  rows={10}
                  className="w-full rounded-md border border-apolo-borderSubtle bg-apolo-bg p-2 font-mono text-[11px] text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
                />
                {schemaDraft.notes && (
                  <details className="rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-2 text-xs text-apolo-textSecondary">
                    <summary className="cursor-pointer select-none">Notas do LLM</summary>
                    <pre className="mt-2 whitespace-pre-wrap text-[11px]">{schemaDraft.notes}</pre>
                  </details>
                )}
                <button
                  type="button"
                  onClick={applySchema}
                  disabled={loading || !createdProject?.id}
                  className="inline-flex items-center gap-1 rounded-md bg-apolo-success px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                >
                  {loading ? <Loader2 size={11} className="animate-spin" /> : <Database size={11} aria-hidden="true" />}
                  Aplicar no projeto
                </button>
              </>
            )}
            {applyResult && (
              <div
                role="alert"
                className={`rounded-md border p-2 text-xs ${
                  applyResult.ok
                    ? 'border-apolo-success/30 bg-apolo-successBg text-apolo-success'
                    : 'border-apolo-error/30 bg-apolo-errorBg text-apolo-error'
                }`}
              >
                {applyResult.ok ? 'Schema aplicado com sucesso.' : applyResult.error}
              </div>
            )}
          </section>
        )}
      </div>
    </dialog>
  );
}
